import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import { authBunja } from '@flipster/features/src/shared/auth/state';
import { useKYC } from '@flipster/features/src/shared/kyc';
import { useUserTier } from '@flipster/features/src/shared/user-tier/state';
import { useCopy } from '@flipster/features/src/shared/webview/clipboard';
import { FormattedMessage } from '@flipster/i18n';
import { Link } from '@flipster/i18n/routing';
import { IconButton } from '@flipster/react/design-system/icon-button';
import { MaskedEmail } from '@flipster/react/design-utils/masking';
import CautionCircleIcon from '@flipster/react/icon/generated/CautionCircleIcon';
import ChevronRightIcon from '@flipster/react/icon/generated/ChevronRightIcon';
import CopyIcon from '@flipster/react/icon/generated/CopyIcon';
import UserVipstatusBasicAccountColorIcon from '@flipster/react/icon/generated/UserVipstatusBasicAccountColorIcon';
import UserVipstatusVipAccountColorIcon from '@flipster/react/icon/generated/UserVipstatusVipAccountColorIcon';
import { css } from '@flipster/styled-system/css';
import { hstack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import segment from '@flipster/utils/segment';
import { ProfileEvent } from '@flipster/utils/segment/events/profile';

import { KycWarning } from '../kyc-status';

const AccountNav = () => {
  const { getMeAtom, isKolAtom } = useBunja(authBunja);
  const isKol = useAtomValue(isKolAtom);
  const { data: user } = useAtomValue(getMeAtom);
  const copy = useCopy(<FormattedMessage id='Autogen_common.r251126.s_UID_copied' />);
  const { isInGracePeriod } = useKYC();
  const { isVip } = useUserTier();

  const copyUid = (uid: string) => {
    copy(uid);
    segment.track(ProfileEvent.UIDCopyClick, { is_menu: true });
  };

  return (
    <Link
      href='/user/account'
      className={hstack({
        justify: 'space-between',
        smallDesktopDown: { py: 8 },
        smallDesktop: {
          pt: 4,
          pb: 16,
          borderBottomWidth: '1px',
          borderBottomStyle: 'solid',
          borderBottomColor: 'border.default.level-1',
        },
      })}
      onClick={() => segment.track(ProfileEvent.AccountInfoTypeClick, { account_info: 'account', is_kol: isKol })}
    >
      <div className={hstack({ gap: 8 })}>
        {isVip ? <UserVipstatusVipAccountColorIcon /> : <UserVipstatusBasicAccountColorIcon />}
        <div>
          <div className={hstack({ gap: 4 })}>
            <MaskedEmail email={user?.email} css={{ textStyle: 'Text/L/Regular', color: 'content.default.level-5' }} />
            {isInGracePeriod && <CautionCircleIcon size={16} color={token('colors.content.accent.sell')} />}
          </div>
          <KycWarning />
          {user?.uid && (
            <div
              className={hstack({ textStyle: 'Text/S/Regular', gap: 4, py: 4 })}
              onClick={(e) => {
                // Prevent route change
                e.preventDefault();
                // Prevent mobile drawer close
                e.stopPropagation();
                copyUid(user.uid);
              }}
            >
              <span className={css({ color: 'content.default.level-2' })}>UID</span>
              <div className={hstack({ color: 'content.default.level-5' })}>
                {user.uid}
                <IconButton icon={<CopyIcon />} hierarchy='quaternary' size='small' />
              </div>
            </div>
          )}
        </div>
      </div>
      <IconButton hierarchy='quaternary' size='small' color='content.default.level-3' icon={<ChevronRightIcon />} />
    </Link>
  );
};

export default AccountNav;
